/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-empty-function */
import {
  createContext,
  FC,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import ErrorBoundary from '@/components/ErrorBoundary';
import { useUserInfo } from '@/hooks/useUserInfo';
import { useI18nContext } from '@/i18n/I18n';
import { getKYCInfo } from '@/services/security';
import { getCaptchaConfig, getRealCookieConfig } from '@/services/user';
import { supportGoogleCapchaHost } from '@/utils/common/oldCaptcha';
import { DEFAULT_CAPTCHA_CONFIG } from '@/utils/constants';
import { SUB_ACCOUNT_LOGIN_CAPTCHA_SCENE } from '@/utils/constants/sub-account';
import { jumpSecurityPage } from '@/utils/sop-grep';

const Captcha = lazy(() => import('@/safe/components/ByCaptcha'));

interface IGlobalContext {
  [key: string]: any;
}
interface IGlobalProvider {
  value: IGlobalContext;
  children: any;
}
export enum CaptchaType {
  GT = 'geecaptcha',
  QQ = 'qqcatpcha',
  GOOGLE = 'recaptcha',
}
interface ICookieConfig {
  completelyUseMagpieCatcha: boolean;
  useDowngrade: boolean;
  guidTailList: string[];
  oldCapchaList: CaptchaType[];
}
const globalContext = createContext<IGlobalContext>({});

const Provider = globalContext.Provider;
globalContext.displayName = 'globalProvider';

const EmptyComp = () => <></>;
let cookieRealConfig: any;
export const GlobalProvider: FC<IGlobalProvider> = ({ children, value }) => {
  // 灰度结束，置为true
  const isGreyUser = true;
  const [loaded, setLoaded] = useState(false);
  const [, lang] = useI18nContext();
  const [count, setCount] = useState(0);
  const [$ti] = useTranslation('inverse');
  const [$te] = useTranslation('inverse-error-code');
  const { user, msgError } = useUserInfo();
  const [cookieConfig, setCookieConfig] = useState<{
    [key: string]: ICookieConfig;
  }>();

  const InputForSafetyComp = useRef<any>(EmptyComp);

  const [queueEvent, setEvent] = useState<undefined | string>();
  const captchaRef = useRef<any>();
  const newCaptchaRef = useRef<any>();

  const [kyc, setKyc] = useState<any>({
    opened: undefined,
    applicant: {
      level: 'LEVEL_DEFAULT',
    },
  });

  const resetEvent = () => setEvent(undefined);

  useLayoutEffect(() => {
    import('@/components/VerifyComp').then(({ InputForSafety }) => {
      InputForSafetyComp.current = InputForSafety;
      setCount(1);
    });
  }, []);

  // useLayoutEffect(() => {
  //   setLoaded(true);
  // }, []);

  useEffect(() => {
    if (!cookieConfig) {
      getCaptchaConfig().then((cookieConfig: any) => {
        cookieRealConfig = cookieConfig;
        setCookieConfig(cookieConfig);
      });
      setTimeout(() => {
        if (!cookieRealConfig) {
          setCookieConfig(getRealCookieConfig(DEFAULT_CAPTCHA_CONFIG));
        }
      }, 2000);
    }
  }, []);
  const toggleUnBindGoogleVisible = () => {
    // TODO 需要灰度测试功能
    jumpSecurityPage(user.id?.toString());
  };

  const callVerifyComp = useCallback(
    (fn: (res: any) => void) => {
      if (window.disabledGoogleCaptcha) {
        return;
      }
      document
        .querySelectorAll(`iframe[src^="https://www.google.com/recaptcha"]`)
        .forEach((node: Element, key: number) => {
          node.parentElement?.classList.add('custom-google-2fa-modal');
        });
      captchaRef?.current?.show(fn);
    },
    [captchaRef, loaded],
  );

  const newCallVerifyComp = useCallback(
    (fn: (res: any) => void) => {
      if (window.disabledGoogleCaptcha) {
        return;
      }
      document
        .querySelectorAll(`iframe[src^="https://www.google.com/recaptcha"]`)
        .forEach((node: Element, key: number) => {
          node.parentElement?.classList.add('custom-google-2fa-modal');
        });
      newCaptchaRef?.current?.show(fn);
    },
    [newCaptchaRef],
  );

  let isLoadSubAccountRecaptcha = false;

  if (window.location.href.indexOf('/app/user/sub-account-login') > 0) {
    isLoadSubAccountRecaptcha = true;
  }

  const getKycData = () => {
    getKYCInfo().then((res) => {
      setKyc({ ...kyc, ...res.result });
    });
  };

  useEffect(() => {
    if (user?.id && !user?.is_submember) {
      getKycData();
    }
  }, [user?.id]);

  const $tFn = (key: string) => {
    if ($ti(key) !== key) {
      return $ti(key);
    }
    if ($te(key) !== key) {
      return $te(key);
    }
    return key;
  };

  const plugins = useMemo(() => {
    const plugins = ['geecaptcha'];
    try {
      const { hostname } = window.location;
      if (supportGoogleCapchaHost.some((host) => hostname.includes(host))) {
        plugins.push('recaptcha');
      }
    } catch (error) {
      /**  ignore */
    }
    return plugins;
  }, []);

  const newCaptchaPlugins = useMemo(() => {
    let plugins: string[] = cookieConfig?.[SUB_ACCOUNT_LOGIN_CAPTCHA_SCENE]
      ?.oldCapchaList || ['qqcaptcha'];
    try {
      const { hostname } = window.location;
      if (
        supportGoogleCapchaHost.every((host) => !hostname.includes(host)) &&
        plugins?.includes('recaptcha')
      ) {
        plugins = ['qqcaptcha'];
      }
    } catch (error) {
      /**  ignore */
    }
    return plugins;
  }, [cookieConfig?.[SUB_ACCOUNT_LOGIN_CAPTCHA_SCENE]?.oldCapchaList]);

  return (
    <Provider
      value={{
        kyc,
        ...value,
        queueEvent,
        resetEvent,
        setEvent,
        callVerifyComp,
        newCallVerifyComp,
        InputForSafetyComp,
        toggleUnBindGoogleVisible,
        $tFn,
        isGreyUser,
        cookieConfig,
        msgError,
        loadCaptcha: () => {
          setLoaded(true);
        },
      }}
    >
      <ErrorBoundary fallback={''}>
        <Suspense fallback="">
          {loaded && (
            <>
              {!isLoadSubAccountRecaptcha && (
                <Captcha
                  key="old"
                  ref={captchaRef}
                  invisibleMode={true}
                  plugins={plugins}
                  lang={lang || 'en'}
                />
              )}

              {isLoadSubAccountRecaptcha && cookieConfig && (
                <Captcha
                  key="new"
                  ref={newCaptchaRef}
                  invisibleMode={true}
                  plugins={newCaptchaPlugins}
                  lang={lang || 'en'}
                />
              )}
            </>
          )}
        </Suspense>
      </ErrorBoundary>
      {children}
    </Provider>
  );
};

export function useStore() {
  return useContext(globalContext);
}
